import React, { useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { RegisterFormData } from '@edytech/shared/validators';
import { registerSchema } from '@edytech/shared/validators';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { OTPInput } from './OTPInput';
import { Mail, Lock, User, Phone, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/auth.service';
import { useNavigate } from 'react-router-dom';

interface RegisterFormProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onClose, onSwitchToLogin }) => {
  const { register: registerUser, error, clearError } = useAuth();
  const [step, setStep] = useState<'details' | 'otp'>('details');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [otpExpiry, setOtpExpiry] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmitDetails = async (data: RegisterFormData) => {
    setIsLoading(true);
    setLocalError(null);
    clearError();
    try {
      // Request OTP via SMS
      const response = await authService.requestOTP(data.phone || '', data.email);
      setPhoneNumber(data.phone || '');
      setOtpExpiry(response.expiresIn);
      setStep('otp');
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : null;
      setLocalError(message || (err instanceof Error ? err.message : 'Failed to send verification code'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPComplete = async (otp: string) => {
    setIsLoading(true);
    setLocalError(null);
    clearError();
    try {
      // Verify OTP
      const verification = await authService.verifyOTP(phoneNumber, otp);
      if (!verification.verified) {
        setLocalError('Invalid verification code. Please try again.');
        setIsLoading(false);
        return;
      }

      // Get form data and register user
      const formData = watch();
      await registerUser({
        name: formData.name,
        email: formData.email,
        phone: phoneNumber,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      onClose();
      navigate('/dashboard');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    setLocalError(null);
    try {
      const response = await authService.requestOTP(phoneNumber, watch('email'));
      setOtpExpiry(response.expiresIn);
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : null;
      setLocalError(message || (err instanceof Error ? err.message : 'Failed to resend code'));
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = error || localError;

  return (
    <div>
      {displayError && (
        <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {displayError}
        </div>
      )}

      {step === 'details' && (
        <form onSubmit={handleSubmit(onSubmitDetails)} className="space-y-4">
          <Input
            label="Full Name"
            placeholder="John Doe"
            icon={<User className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
            {...registerField('name')}
            error={errors.name?.message}
          />
          <Input
            label="Email Address"
            type="email"
            placeholder="you@example.com"
            icon={<Mail className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
            {...registerField('email')}
            error={errors.email?.message}
          />
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+233 XX XXX XXXX"
            icon={<Phone className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
            {...registerField('phone')}
            error={errors.phone?.message}
          />
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            icon={<Lock className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
            {...registerField('password')}
            error={errors.password?.message}
            endAdornment={<button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
          />
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="••••••••"
            icon={<Lock className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
            {...registerField('confirmPassword')}
            error={errors.confirmPassword?.message}
            endAdornment={<button type="button" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} onClick={() => setShowConfirmPassword((visible) => !visible)}>{showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
          />
          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
            {isLoading ? 'Sending Code...' : 'Create Account'}
          </Button>
        </form>
      )}

      {step === 'otp' && (
        <div className="space-y-6">
          <p className="text-sm text-[rgb(var(--light-secondary))]">
            We've sent a 6‑digit verification code to{' '}
            <span className="font-medium text-[rgb(var(--foreground))]">{phoneNumber}</span>.
            Please enter it below.
          </p>
          {otpExpiry && (
            <p className="text-center text-xs text-[rgb(var(--light-secondary))]">
              Code expires in {Math.floor(otpExpiry / 60)} minutes
            </p>
          )}
          <OTPInput length={6} onComplete={handleOTPComplete} isDisabled={isLoading} />
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="flex items-center text-sm text-[rgb(var(--light-secondary))] hover:text-primary"
              onClick={() => setStep('details')}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Change phone number
            </button>
            <button
              type="button"
              className="text-sm text-primary hover:underline disabled:opacity-50"
              onClick={handleResendOTP}
              disabled={isLoading}
            >
              Resend Code
            </button>
          </div>
          {isLoading && (
            <p className="text-center text-sm text-[rgb(var(--light-secondary))]">Verifying...</p>
          )}
        </div>
      )}

      <p className="mt-4 text-center text-sm text-[rgb(var(--light-secondary))]">
        Already have an account?{' '}
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={onSwitchToLogin}
        >
          Log in
        </button>
      </p>
    </div>
  );
};